import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ChangeBorrowerStatus } from 'app/store/borrower/borrower/borrower.actions';
import { StatusOption, borrowerStatusOptions } from 'app/services/borrower-status/status-options';
import { BorrowerStatusChange } from '../borrower-status-picker/borrower-status-picker.component';
import { Borrower } from 'app/models/dto/borrower.model';
import { DealState } from 'app/store/deal/deal/deal.state';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { CallState } from 'app/store/call/call.state';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { notNull } from 'ng2-app/modules/shared/functions/filters';

@Component({
  selector: 'app-borrower-status-picker-smart',
  templateUrl: './borrower-status-picker-smart.component.html',
  styleUrls: ['./borrower-status-picker-smart.component.scss']
})
export class BorrowerStatusPickerSmartComponent implements OnInit, OnDestroy {
  @Select(CallState.isOnCall) isOnCall$: Observable<boolean>;
  @Select(BorrowerValueState.complianceReview) inComplianceReview$: Observable<boolean>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
  options$: Observable<StatusOption[]>;
  disabled$: Observable<boolean>;
  disabledReason$: Observable<string | null>;
  destroyed$ = new Subject<boolean>();

  constructor(
    private store: Store
  ) { }

  ngOnInit(): void {
    // rely on NGXS borrower only
    this.options$ = this.getStatusOptions();
    this.disabledReason$ = this.getDisabledReason();
    this.disabled$ = this.disabledReason$.pipe(map(x => !!x));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  handleChangeStatus({ status, reason, dncParams }: BorrowerStatusChange) {
    this.borrower$.pipe(take(1)).subscribe(borrower => {
      this.store.dispatch(new ChangeBorrowerStatus(
        borrower.id,
        status,
        reason,
        dncParams
      ));
    });
  }

  /**
   * See if a borrower has already reached or passed another status.
   */
  statusSameOrGreaterThan(borrowerStatus: string, thresholdStatus: string): boolean {
    const borrowerStatusPosition = borrowerStatusOptions.findIndex(item => item.value === borrowerStatus);
    const thresholdStatusPosition = borrowerStatusOptions.findIndex(item => item.value === thresholdStatus);
    return borrowerStatusPosition >= thresholdStatusPosition;
  }

  private getStatusOptions(): Observable<StatusOption[]> {
    return combineLatest([
      this.borrower$.pipe(filter(notNull)),
      this.store.select(DealState.activeDeals).pipe(map(deals => deals.length > 0)),
      this.store.select(DealState.fundedDeals).pipe(map(deals => deals.length > 0)),
      this.lenderUser$
    ]).pipe(
      map(([
        borrower,
        hasActiveDeal,
        hasFundedDeal,
        lenderUser
      ]) => borrowerStatusOptions
          .filter(option => this.isOptionVisible(borrower.status, hasFundedDeal, option))
          .map(option => {
            option.disabledReason = this.optionDisabledReason(
              lenderUser?.permissions.includes('canManuallyChangeBorrowerStatus'),
              borrower.status,
              lenderUser?.permissions.includes('deactivateBorrowerStatus'),
              hasActiveDeal,
              hasFundedDeal,
              lenderUser?.permissions.includes('movePreviouslyFundedToFunded'),
              option
            );
            return option;
          }))
    );
  }

  private isOptionVisible(status: string, hasFundedDeal: boolean, option: StatusOption): boolean {
    return (
      status === option.value
      || (option.value === 'notInterestedInRenewal' && hasFundedDeal)
      || !option.hidden
    );
  }

  // When generating list of borrower status options, if option is disabled,
  // this returns the reason, or null for valid options.
  private optionDisabledReason(
    canManuallyChangeBorrowerStatus: boolean,
    status: string,
    deactivateBorrowerStatus: boolean,
    hasActiveDeal: boolean,
    hasFundedDeal: boolean,
    movePreviouslyFundedToFunded,
    option: StatusOption
  ): string | null {
    if (option.stage === 'inactive' && hasActiveDeal) {
      return 'This borrower has active deals.';
    }

    if (
      option.stage === 'funded'
      && !(hasFundedDeal && movePreviouslyFundedToFunded)
    ) {
      return 'You do not have permission to move this borrower to funded manually.';
    }

    // Disallow if borrower status already at attempted or later.
    if ( (!canManuallyChangeBorrowerStatus && deactivateBorrowerStatus) &&
    option.value === 'noAttempt' && this.statusSameOrGreaterThan(status, 'attempted')) {
      return 'You have already attempted to contact this borrower';
    }

    // Disallow if borrower status already at inContact or later.
    if ((!canManuallyChangeBorrowerStatus && deactivateBorrowerStatus) &&
    option.value === 'attempted' && this.statusSameOrGreaterThan(status, 'inContact')) {
      return 'You have already contacted this borrower';
    }

    return null;
  }

  private getDisabledReason(): Observable<string | null> {
    return combineLatest([
      this.isOnCall$,
      this.borrower$,
      this.inComplianceReview$,
      this.lenderUser$
    ]).pipe(
      map(args => this.calculateDisabledReason.apply(this, args))
    );
  }

  private calculateDisabledReason(
    isOnCall: boolean,
    borrower: Borrower,
    inComplianceReview: boolean,
    lenderUser: LenderUser
  ): string | null {
    if (!borrower) {
      return 'Loading.';
    }

    if (isOnCall) {
      return 'Please finish call before changing borrower status.';
    }

    if (borrower.status === 'dead') {
      if (lenderUser.permissions.includes('reactivateBorrower')) {
        return 'Please use Reactivate Business action to move from "Dead" status.';
      } else {
        return 'You do not have permission to move borrower from "Dead" status.';
      }
    }

    if (borrower.status === 'doNotContact') {
      return 'You do not have permission to move borrower from "doNotContact" status.';
    }

    if (inComplianceReview) {
      return 'This borrower is in compliance review.';
    }

    return null;
  }
}
